'use client';

import { useState, useEffect } from 'react';
import { Link2, Users, Target, Map, Film, Share2, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthToken } from '@/hooks/use-auth';

// ============================================
// Types
// ============================================

interface RelatedArtifact {
  nodeId: string;
  nodeType: string;
  name: string;
  artifactType: string;
  artifactId: string;
  relationPath: string[];
  depth: number;
}

interface RelatedArtifactsPanelProps {
  projectId: string;
  artifactType: string;
  artifactId: string;
  className?: string;
}

// ============================================
// Constants
// ============================================

const ARTIFACT_TYPE_ICONS: Record<string, React.ElementType> = {
  PERSONA: Users,
  USE_CASE: Target,
  JOURNEY: Share2,
  SERVICE_MAP: Map,
  STORYBOARD: Film,
};

const ARTIFACT_TYPE_LABELS: Record<string, string> = {
  PERSONA: 'Persona',
  USE_CASE: 'Use Case',
  JOURNEY: 'Journey',
  SERVICE_MAP: 'Service Map',
  STORYBOARD: 'Storyboard',
};

const ARTIFACT_TYPE_ROUTES: Record<string, string> = {
  PERSONA: 'personas',
  USE_CASE: 'use-cases',
  JOURNEY: 'journeys',
  SERVICE_MAP: 'service-maps',
  STORYBOARD: 'storyboards',
};

const RELATION_LABELS: Record<string, string> = {
  HAS_NEED: 'shares need with',
  HAS_PAIN: 'shares pain point with',
  HAS_GOAL: 'shares goal with',
  PARTICIPATES_IN: 'participates in',
  INVOLVES_PERSONA: 'involves',
  HAS_ACTOR: 'has actor',
  CONTAINS_STEP: 'contains step in',
  HAS_TOUCHPOINT: 'has touchpoint',
  ADDRESSES_NEED: 'addresses need from',
  SOLVES_PAIN: 'solves pain from',
  DERIVED_FROM: 'derived from',
  IMPLEMENTS: 'implements',
  FOR_PERSONA: 'for',
  RELATED_TO: 'related to',
};

// ============================================
// Component
// ============================================

export function RelatedArtifactsPanel({
  projectId,
  artifactType,
  artifactId,
  className,
}: RelatedArtifactsPanelProps) {
  const [related, setRelated] = useState<RelatedArtifact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const token = useAuthToken();

  const fetchRelated = async () => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/projects/${projectId}/graph/related/${artifactType}/${artifactId}?maxDepth=2`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch related artifacts');
      }

      const data = await response.json();
      setRelated(data.data?.related || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load related artifacts');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRelated();
  }, [projectId, artifactType, artifactId, token]);

  // Group related by artifact type
  const groupedRelated = related.reduce((acc, item) => {
    if (!acc[item.artifactType]) {
      acc[item.artifactType] = [];
    }
    acc[item.artifactType].push(item);
    return acc;
  }, {} as Record<string, RelatedArtifact[]>);

  const hasRelated = related.length > 0;

  return (
    <div className={cn('bg-white rounded-xl border border-gray-200', className)}>
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">Related Artifacts</span>
          {hasRelated && (
            <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
              {related.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              fetchRelated();
            }}
            className="p-1 hover:bg-gray-100 rounded"
            title="Refresh"
          >
            <RefreshCw className={cn('w-3.5 h-3.5 text-gray-400', isLoading && 'animate-spin')} />
          </button>
          <ChevronRight
            className={cn('w-4 h-4 text-gray-400 transition-transform', isExpanded && 'rotate-90')}
          />
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4">
          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          )}

          {/* Error */}
          {error && !isLoading && (
            <div className="text-sm text-red-500 text-center py-4">{error}</div>
          )}

          {/* Empty State */}
          {!isLoading && !error && !hasRelated && (
            <div className="text-sm text-gray-500 text-center py-4">
              No related artifacts found.
              <br />
              <span className="text-xs text-gray-400">
                Relations are extracted from artifact content automatically.
              </span>
            </div>
          )}

          {/* Related Artifacts */}
          {!isLoading && !error && hasRelated && (
            <div className="space-y-4">
              {Object.entries(groupedRelated).map(([type, items]) => {
                const Icon = ARTIFACT_TYPE_ICONS[type] || Link2;
                const label = ARTIFACT_TYPE_LABELS[type] || type;
                const route = ARTIFACT_TYPE_ROUTES[type] || type.toLowerCase();

                return (
                  <div key={type}>
                    <div className="flex items-center gap-1.5 mb-2">
                      <Icon className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs font-medium text-gray-500">{label}s</span>
                    </div>
                    <div className="space-y-1.5">
                      {items.map((item) => {
                        const relationLabel =
                          item.relationPath.length > 0
                            ? RELATION_LABELS[item.relationPath[0]] || 'related to'
                            : 'related to';

                        return (
                          <a
                            key={`${item.artifactId}-${item.nodeId}`}
                            href={`/projects/${projectId}/definition/${route}?item=${item.artifactId}`}
                            className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 group"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-gray-900 truncate">{item.name}</div>
                              <div className="text-xs text-gray-400 truncate">
                                {relationLabel}
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RelatedArtifactsPanel;
